import { useState, useEffect, useCallback } from 'react';

export type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'unknown';

export type BrowserType = 'samsung' | 'chrome' | 'firefox' | 'safari' | 'edge' | 'opera' | 'unknown';

interface LocationPermissionState {
  status: PermissionStatus;
  isLoading: boolean;
  isIOSPWA: boolean;
  isAndroidPWA: boolean;
  isPWA: boolean;
  error: string | null;
  browserType: BrowserType;
  browserName: string;
}

// Detect browser type
const detectBrowser = (): { type: BrowserType; name: string } => {
  const ua = navigator.userAgent;
  
  if (ua.includes('SamsungBrowser')) {
    return { type: 'samsung', name: 'Samsung Internet' };
  }
  if (ua.includes('OPR') || ua.includes('Opera')) {
    return { type: 'opera', name: 'Opera' };
  }
  if (ua.includes('Edg')) {
    return { type: 'edge', name: 'Microsoft Edge' };
  }
  if (ua.includes('Firefox')) {
    return { type: 'firefox', name: 'Firefox' };
  }
  if (ua.includes('Safari') && !ua.includes('Chrome')) {
    return { type: 'safari', name: 'Safari' };
  }
  if (ua.includes('Chrome')) {
    return { type: 'chrome', name: 'Chrome' };
  }
  return { type: 'unknown', name: '브라우저' };
};

export function useLocationPermission() {
  const browser = detectBrowser();
  
  const [state, setState] = useState<LocationPermissionState>({
    status: 'unknown',
    isLoading: true,
    isIOSPWA: false,
    isAndroidPWA: false,
    isPWA: false,
    error: null,
    browserType: browser.type,
    browserName: browser.name,
  });

  // Detect PWA mode
  const detectPWAMode = useCallback(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);

    return {
      isIOSPWA: isIOS && (isStandalone || isIOSStandalone),
      isAndroidPWA: isAndroid && isStandalone,
      isPWA: isStandalone || isIOSStandalone,
    };
  }, []);

  // Check permission status
  const checkPermission = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    const pwaMode = detectPWAMode();

    // Check if Geolocation API is available
    if (!('geolocation' in navigator)) {
      setState(prev => ({
        ...prev,
        status: 'denied',
        isLoading: false,
        error: 'Geolocation is not supported by this browser',
        ...pwaMode,
      }));
      return;
    }

    // Try to use Permissions API first (not supported in all browsers)
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        
        setState(prev => ({
          ...prev,
          status: result.state as PermissionStatus,
          isLoading: false,
          error: null,
          ...pwaMode,
        }));

        // Listen for permission changes
        result.onchange = () => {
          setState(prev => ({
            ...prev,
            status: result.state as PermissionStatus,
          }));
        };

        return;
      } catch (err) {
      }
    }

    // Fallback: Try to get position to determine permission
    // For iOS Safari/PWA, this is the only reliable method
    try {
      await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 60000,
        });
      });

      setState(prev => ({
        ...prev,
        status: 'granted',
        isLoading: false,
        error: null,
        ...pwaMode,
      }));
    } catch (err: any) {
      const errorStatus: PermissionStatus = err.code === 1 ? 'denied' : 'prompt';
      setState(prev => ({
        ...prev,
        status: errorStatus,
        isLoading: false,
        error: err.code === 1 ? 'Location permission denied' : null,
        ...pwaMode,
      }));
    }
  }, [detectPWAMode]);

  // Request permission (triggers browser prompt)
  const requestPermission = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    const pwaMode = detectPWAMode();

    try {
      await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      setState(prev => ({
        ...prev,
        status: 'granted',
        isLoading: false,
        error: null,
        ...pwaMode,
      }));
      return true;
    } catch (err: any) {
      let errorMessage = 'Location permission request failed';
      
      if (err.code === 1) {
        errorMessage = pwaMode.isIOSPWA 
          ? 'Location access denied. Please follow the iOS settings guide below.'
          : 'Location access denied. Please enable in browser settings.';
      } else if (err.code === 2) {
        errorMessage = 'Location unavailable. Please check your device settings.';
      } else if (err.code === 3) {
        errorMessage = 'Location request timed out. Please try again.';
      }

      setState(prev => ({
        ...prev,
        status: err.code === 1 ? 'denied' : 'prompt',
        isLoading: false,
        error: errorMessage,
        ...pwaMode,
      }));
      return false;
    }
  }, [detectPWAMode]);

  // Test location - actually try to get position and return result
  const testLocation = useCallback(async (): Promise<{
    success: boolean;
    coords?: { latitude: number; longitude: number; accuracy: number };
    error?: string;
  }> => {
    const pwaMode = detectPWAMode();
    
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        });
      });

      setState(prev => ({
        ...prev,
        status: 'granted',
        error: null,
        ...pwaMode,
      }));

      return {
        success: true,
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        },
      };
    } catch (err: any) {
      let errorMessage = 'Failed to get location';
      
      if (err.code === 1) {
        if (pwaMode.isIOSPWA) {
          errorMessage = 'Location denied. Go to iOS Settings > Safari > Location to enable.';
        } else {
          errorMessage = 'Location denied. Enable in browser settings.';
        }
        setState(prev => ({ ...prev, status: 'denied', error: errorMessage, ...pwaMode }));
      } else if (err.code === 2) {
        errorMessage = 'Location unavailable. Check GPS/Wi-Fi.';
      } else if (err.code === 3) {
        errorMessage = 'Location timed out. Try again in open area.';
      }

      return { success: false, error: errorMessage };
    }
  }, [detectPWAMode]);

  // Initial check
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Get guidance text for PWA users - IMPROVED for both iOS and Android with browser detection
  const getPWAGuidance = useCallback(() => {
    if (!state.isPWA && !state.isIOSPWA && !state.isAndroidPWA) return null;
    
    const browserName = state.browserName;
    
    if (state.status === 'denied' || state.status === 'prompt') {
      if (state.isIOSPWA) {
        return {
          platform: 'ios',
          title: 'iOS PWA 위치 권한 활성화',
          note: '⚠️ PWA 앱은 iOS 설정에서 별도 앱으로 나타나지 않습니다. Safari 설정을 사용하세요.',
          browserName: 'Safari',
          methods: [
            {
              title: '방법 1: Safari 설정',
              steps: [
                '설정(Settings) 앱 열기',
                '"Safari" 탭',
                '"웹사이트 설정(Settings for Websites)" 섹션',
                '"위치(Location)" 탭',
                '"허용(Allow)" 또는 "확인(Ask)" 선택',
              ],
            },
            {
              title: '방법 2: 개인정보 설정',
              steps: [
                '설정(Settings) > 개인정보 및 보안(Privacy & Security)',
                '"위치 서비스(Location Services)" 탭',
                '위치 서비스가 켜져있는지 확인',
                '"Safari 웹사이트(Safari Websites)" 찾아서 탭',
                '"앱 사용 중(While Using)" 선택',
              ],
            },
          ],
        };
      } else if (state.isAndroidPWA) {
        return {
          platform: 'android',
          title: 'Android PWA 위치 권한 활성화',
          note: `⚠️ PWA 앱은 설정에서 별도 앱으로 나타나지 않습니다. "${browserName}" 앱의 위치 권한을 설정하세요.`,
          browserName,
          methods: [
            {
              title: `방법 1: ${browserName} 앱 권한 설정`,
              steps: [
                '설정(Settings) > 앱(Apps) 열기',
                `"${browserName}" 찾아서 탭`,
                '"권한(Permissions)" 탭',
                '"위치(Location)" 탭',
                '"앱 사용 중에만 허용(Allow only while using)" 선택',
              ],
            },
            {
              title: '방법 2: 위치 설정에서 브라우저 찾기',
              steps: [
                '설정(Settings) > 위치(Location) 열기',
                '"앱 권한(App permissions)" 탭',
                '"사용 중에만 허용됨" 또는 "허용 안 함" 목록 확인',
                `"${browserName}" 찾아서 탭`,
                '"앱 사용 중에만 허용" 선택',
              ],
            },
          ],
        };
      }
    }
    return null;
  }, [state.isIOSPWA, state.isAndroidPWA, state.isPWA, state.status, state.browserName]);

  // Legacy function for backwards compatibility
  const getIOSGuidance = useCallback(() => {
    const guidance = getPWAGuidance();
    if (!guidance) return null;
    
    // Return legacy format
    return {
      title: guidance.title,
      note: guidance.note,
      methods: guidance.methods,
      steps: guidance.methods?.[0]?.steps || [],
    };
  }, [getPWAGuidance]);

  return {
    ...state,
    checkPermission,
    requestPermission,
    testLocation,
    getIOSGuidance,
    getPWAGuidance,
  };
}
